import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { SessionProvider, ToastProvider, useSession } from './lib/store';
import { ChatProvider } from './lib/chat';
import { Shell } from './components/Shell';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoginPage } from './pages/Login';
import { HomePage } from './pages/Home';
import { HatcheryPage } from './pages/Hatchery';
import { GrovePage } from './pages/Grove';
import { EggMarketPage } from './pages/EggMarket';
import { CollectionPage } from './pages/Collection';
import { PokemonDetailPage } from './pages/PokemonDetail';
import { DexPage, DexEntryPage } from './pages/Dex';
import { CommunityPage, LeaderboardPage } from './pages/Community';
import { TrainerProfilePage } from './pages/TrainerProfile';
import { ShopPage } from './pages/Shop';
import { AchievementsPage } from './pages/Achievements';
import { BadgesPage } from './pages/Badges';
import { EventsPage, EventDetailPage } from './pages/Events';
import { WorldBossPage } from './pages/WorldBoss';
import { TradesPage } from './pages/Trades';
import { NurseryPage } from './pages/Nursery';
import { BreederPage } from './pages/Breeder';
import { WarsPage } from './pages/Wars';
import { JournalFamilyPage, JournalPageIndex } from './pages/Journal';
import { GuildsPage } from './pages/Guilds';
import { GuildPage } from './pages/Guild';
import { BazaarPage } from './pages/Bazaar';
import { MyBazaarPage } from './pages/MyBazaar';
import { BazaarShopPage } from './pages/BazaarShop';
import { FriendsPage } from './pages/Friends';
import { SettingsPage } from './pages/Settings';
import { ClaimPage } from './pages/Claim';
import { AdminPage } from './pages/Admin';
import { AdminEventsPage } from './pages/AdminEvents';
import { AuditPage, RoleAdminPage, TeamPage } from './pages/Roles';
import { StaffPage } from './pages/Staff';
import { MuseumPage } from './pages/Museum';
import { CompanionsPage } from './pages/Companions';
import { StatisticsPage } from './pages/Statistics';
import { HallOfFamePage } from './pages/HallOfFame';
import { EncyclopediaPage } from './pages/Encyclopedia';
import { CustomizePage } from './pages/Customize';
import { CreditsPage } from './pages/Credits';

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <SessionProvider>
          <Router />
        </SessionProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

function Router() {
  const { me, loading } = useSession();
  const location = useLocation();

  if (loading) return <BootScreen />;
  if (!me) return <LoginPage />;

  return (
    // The chat provider wraps the Shell rather than living inside a page, so
    // the socket, the backlog and the unread counts survive navigation. A chat
    // that reconnects every time you open your Collection is not a chat.
    <ChatProvider enabled={Boolean(me)}>
    <Shell>
      {/* Keyed by pathname so navigating away from a crashed page recovers. */}
      <ErrorBoundary key={location.pathname}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/grove" element={<GrovePage />} />
        <Route path="/grove/:username" element={<GrovePage />} />
        <Route path="/hatchery" element={<HatcheryPage />} />
        <Route path="/eggs" element={<EggMarketPage />} />
        <Route path="/bazaar" element={<BazaarPage />} />
        {/* /mine before /:username so the shop route never swallows it. */}
        <Route path="/bazaar/mine" element={<MyBazaarPage />} />
        <Route path="/bazaar/:username" element={<BazaarShopPage />} />
        <Route path="/guilds" element={<GuildsPage />} />
        <Route path="/guilds/:key" element={<GuildPage />} />
        <Route path="/journal" element={<JournalPageIndex />} />
        <Route path="/journal/:key" element={<JournalFamilyPage />} />
        <Route path="/collection" element={<CollectionPage />} />
        <Route path="/pokemon/:id" element={<PokemonDetailPage />} />
        <Route path="/dex" element={<DexPage />} />
        <Route path="/dex/:id" element={<DexEntryPage />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/trainer/:username" element={<TrainerProfilePage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/achievements" element={<AchievementsPage />} />
        <Route path="/badges" element={<BadgesPage />} />
        <Route path="/museum" element={<MuseumPage />} />
        <Route path="/museum/:username" element={<MuseumPage />} />
        <Route path="/companions" element={<CompanionsPage />} />
        <Route path="/companions/:username" element={<CompanionsPage />} />
        <Route path="/statistics" element={<StatisticsPage />} />
        <Route path="/statistics/:username" element={<StatisticsPage />} />
        <Route path="/hall-of-fame" element={<HallOfFamePage />} />
        <Route path="/encyclopedia" element={<EncyclopediaPage />} />
        <Route path="/customise" element={<CustomizePage />} />
        {/* The other spelling, because half the world types it. */}
        <Route path="/customize" element={<Navigate to="/customise" replace />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/world-boss" element={<WorldBossPage />} />
        <Route path="/events/:key" element={<EventDetailPage />} />
        <Route path="/trades" element={<TradesPage />} />
        <Route path="/nursery" element={<NurseryPage />} />
        <Route path="/breeder" element={<BreederPage />} />
        <Route path="/wars" element={<WarsPage />} />
        {/* The Daycare's old URL still exists in bookmarks and old links. */}
        <Route path="/daycare" element={<Navigate to="/nursery" replace />} />
        <Route path="/friends" element={<FriendsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/claim" element={<ClaimPage />} />
        {/* The team page is public — the point of a Founder ribbon is that
            other people see it. */}
        <Route path="/team" element={<TeamPage />} />
        {/* Credits, for the same reason: attribution nobody can reach is not
            attribution. It is inside the shell because that is where a player
            follows the link from, and the endpoint behind it is public. */}
        <Route path="/credits" element={<CreditsPage />} />
        {/* Everything below is gated on a *permission*, never on a role name.
            This is presentation only: it stops a player being shown a panel
            they cannot use. Every one of these endpoints checks again on the
            server, because anything the browser decides is a suggestion. */}
        {me.permissions?.includes('staff.panel') && <Route path="/staff" element={<StaffPage />} />}
        {me.permissions?.includes('roles.manage') && (
          <Route path="/admin/roles" element={<RoleAdminPage />} />
        )}
        {me.permissions?.includes('logs.view') && <Route path="/admin/audit" element={<AuditPage />} />}
        {me.permissions?.includes('admin.panel') && <Route path="/admin" element={<AdminPage />} />}
        {(me.permissions?.includes('events.view') || me.permissions?.includes('events.manage')) && (
          <Route path="/admin/events" element={<AdminEventsPage />} />
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ErrorBoundary>
    </Shell>
    </ChatProvider>
  );
}

/** Shown for the one round trip it takes to resolve the session cookie. */
function BootScreen() {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <div className="center stack-sm">
        <div
          style={{
            width: 48,
            height: 48,
            margin: '0 auto',
            background:
              'radial-gradient(circle at 50% 50%, var(--paper) 0 20%, var(--ink) 21% 30%, transparent 31%), linear-gradient(180deg, var(--berry) 0 47%, var(--ink) 47% 53%, var(--paper) 53% 100%)',
            boxShadow: '0 0 0 3px var(--ink)',
            animation: 'spin-step 1.2s steps(8) infinite',
          }}
          aria-hidden
        />
        <span className="mono tiny" style={{ color: 'rgba(253,243,223,.7)' }}>
          Loading PokéGrove...
        </span>
      </div>
    </div>
  );
}
